'use client';
import { WEB_RECOVERY_TIME_BETWEEN_MODALS } from '@capsicum/common';
import { Button, GenericDrawer, ModalService, RichText, TextButton } from '@capsicum/common-web';
import { type GenericAgreementProps, useGenericAgreement } from '@capsicum/features/regulations/shared';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useGenericAgreementImages } from '../hooks/useGenericAgreementImages';

import { CredentialGuardLoader } from './CredentialGuardLoader';

export const BASE_TEST_ID = 'requirements-agreement-modal';

export const GenericAgreement = ({ isForcedRequirement, onDismiss, requirement }: GenericAgreementProps) => {
	const {
		acceptAgreement,
		agreeButtonText,
		cancelAgreement,
		confirmAgreeButtonText,
		confirmAgreement,
		confirmDisagreeButtonText,
		confirmSubtitle,
		confirmTitle,
		declineAgreeButtonText,
		declineDisagreeButtonText,
		declineSubtitle,
		declineTitle,
		disagreeButtonText,
		isLoading,
		sameButtonStyles,
		subtitle,
		title
	} = useGenericAgreement(requirement);
	const { confirmImage, declineImage, image } = useGenericAgreementImages(requirement);

	const getDescriptionWithLink = (text: string) => (
		<S.Description
			interpolate={{
				link: ({ href, text }) => (
					<S.Link href={href!} target="_blank">
						{text}
					</S.Link>
				)
			}}
		>
			{text}
		</S.Description>
	);

	const showInitialModal = () =>
		ModalService.addModal({
			detached: true,
			isClosable: !isForcedRequirement,
			onDismiss: () => setTimeout(() => onDismiss(showInitialModal), WEB_RECOVERY_TIME_BETWEEN_MODALS),
			renderComponent: () => (
				<GenericDrawer testID={`${BASE_TEST_ID}-${requirement.identifier}`}>
					{image}
					{title && <S.Title>{title}</S.Title>}
					{getDescriptionWithLink(subtitle)}
				</GenericDrawer>
			),
			renderFooter: ({ close }) => (
				<S.ButtonWrapper>
					<Button
						disabled={isLoading}
						fullWidth
						testID={`${BASE_TEST_ID}-agree-button-${requirement.identifier}`}
						text={agreeButtonText}
						onClick={() =>
							acceptAgreement({ close, delay: WEB_RECOVERY_TIME_BETWEEN_MODALS, showConfirmModal })
						}
					/>
					{sameButtonStyles ? (
						<Button
							disabled={isLoading}
							fullWidth
							testID={`${BASE_TEST_ID}-disagree-button-${requirement.identifier}`}
							text={disagreeButtonText}
							onClick={() =>
								cancelAgreement({ close, delay: WEB_RECOVERY_TIME_BETWEEN_MODALS, showDeclineModal })
							}
						/>
					) : (
						<TextButton
							color="neutral"
							disabled={isLoading}
							testID={`${BASE_TEST_ID}-disagree-text-button-${requirement.identifier}`}
							text={disagreeButtonText}
							onClick={() =>
								cancelAgreement({ close, delay: WEB_RECOVERY_TIME_BETWEEN_MODALS, showDeclineModal })
							}
						/>
					)}
				</S.ButtonWrapper>
			)
		});

	const showConfirmModal = () =>
		ModalService.addModal({
			detached: true,
			isClosable: false,
			onDismiss: () => setTimeout(showInitialModal, WEB_RECOVERY_TIME_BETWEEN_MODALS),
			renderComponent: () => (
				<GenericDrawer testID={`${BASE_TEST_ID}-confirm-${requirement.identifier}`}>
					{confirmImage}
					{confirmTitle && <S.Title>{confirmTitle}</S.Title>}
					{getDescriptionWithLink(confirmSubtitle)}
				</GenericDrawer>
			),
			renderFooter: ({ close }) => (
				<S.ButtonWrapper>
					<Button
						disabled={isLoading}
						fullWidth
						testID={`${BASE_TEST_ID}-confirm-agree-button-${requirement.identifier}`}
						text={confirmAgreeButtonText}
						onClick={() => confirmAgreement({ accepted: true, close })}
					/>
					{sameButtonStyles ? (
						<Button
							disabled={isLoading}
							fullWidth
							testID={`${BASE_TEST_ID}-confirm-disagree-button-${requirement.identifier}`}
							text={confirmDisagreeButtonText}
							onClick={() => close()}
						/>
					) : (
						<TextButton
							color="neutral"
							disabled={isLoading}
							testID={`${BASE_TEST_ID}-confirm-disagree-text-button-${requirement.identifier}`}
							text={confirmDisagreeButtonText}
							onClick={() => close()}
						/>
					)}
				</S.ButtonWrapper>
			)
		});

	const showDeclineModal = () =>
		ModalService.addModal({
			detached: true,
			isClosable: false,
			onDismiss: () => setTimeout(showInitialModal, WEB_RECOVERY_TIME_BETWEEN_MODALS),
			renderComponent: () => (
				<GenericDrawer testID={`${BASE_TEST_ID}-decline-${requirement.identifier}`}>
					{declineImage}
					{declineTitle && <S.Title>{declineTitle}</S.Title>}
					{getDescriptionWithLink(declineSubtitle)}
				</GenericDrawer>
			),
			renderFooter: ({ close }) => (
				<S.ButtonWrapper>
					<Button
						disabled={isLoading}
						fullWidth
						testID={`${BASE_TEST_ID}-decline-agree-button-${requirement.identifier}`}
						text={declineAgreeButtonText}
						onClick={() => confirmAgreement({ accepted: false, close })}
					/>
					{sameButtonStyles ? (
						<Button
							disabled={isLoading}
							fullWidth
							testID={`${BASE_TEST_ID}-decline-disagree-button-${requirement.identifier}`}
							text={declineDisagreeButtonText}
							onClick={() => close()}
						/>
					) : (
						<TextButton
							color="neutral"
							disabled={isLoading}
							testID={`${BASE_TEST_ID}-decline-disagree-text-button-${requirement.identifier}`}
							text={declineDisagreeButtonText}
							onClick={() => close()}
						/>
					)}
				</S.ButtonWrapper>
			)
		});

	useEffect(() => {
		showInitialModal();
	}, []);

	return <CredentialGuardLoader />;
};

namespace S {
	export const ButtonWrapper = styled.div`
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
	`;

	export const Description = styled(RichText)`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		margin-top: ${({ theme }) => theme.spacings.xsmall};
		margin: 0;
	`;

	export const Title = styled.p`
		${({ theme }) => theme.fonts.headings.title6};
		text-align: center;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		white-space: pre-line;
	`;

	export const Link = styled.a`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		${({ theme }) => theme.fonts.text.text3};
		text-decoration: none;
	`;
}
